<script setup lang="ts">
import { TabsTrigger as RadixTabsTrigger } from 'radix-vue'
import { inject, computed, type Ref } from 'vue'

interface TabsStyles {
  line: {
    list: string
    trigger: string[]
  }
  solid: {
    list: string
    trigger: string[]
  }
}

type TabVariant = keyof TabsStyles

const defaultStyles: TabsStyles = {
  line: {
    list: '',
    trigger: [
      '-mb-px items-center justify-center whitespace-nowrap border-b-2 border-transparent px-3 pb-2 text-sm font-medium transition-all',
      'text-gray-500 dark:text-gray-500',
      'hover:text-gray-700 hover:dark:text-gray-400',
      'hover:border-gray-300 hover:dark:border-gray-400',
      'data-[state=active]:border-blue-500 data-[state=active]:text-blue-500',
      'data-[state=active]:dark:border-blue-500 data-[state=active]:dark:text-blue-500',
      'data-[disabled]:pointer-events-none data-[disabled]:text-gray-300 data-[disabled]:dark:text-gray-700',
    ],
  },
  solid: {
    list: '',
    trigger: [
      'inline-flex items-center justify-center whitespace-nowrap rounded px-3 py-1 text-sm font-medium ring-1 ring-inset transition-all',
      'text-gray-500 dark:text-gray-400',
      'hover:text-gray-700 hover:dark:text-gray-200',
      'ring-transparent',
      'data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow',
      'data-[state=active]:dark:bg-gray-950 data-[state=active]:dark:text-gray-50',
      'data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 data-[disabled]:opacity-50 data-[disabled]:dark:text-gray-600',
    ],
  },
}

const props = defineProps<{
  value: string
  class?: string
}>()

const variant = inject<Ref<TabVariant>>(
  'tabs-variant',
  computed(() => 'line'),
)
const styles = inject<Ref<TabsStyles>>(
  'tabs-styles',
  computed(() => defaultStyles),
)

const triggerStyles = computed(() => styles.value[variant.value].trigger)
</script>

<template>
  <RadixTabsTrigger :value="props.value" :class="[triggerStyles, props.class]">
    <slot />
  </RadixTabsTrigger>
</template>
